/**
 * Sealed-volume gas pressure and rupture.
 *
 * A flood fill identifies connected gas volumes bounded by SEALED material.
 * Only one volume is evaluated per tick, round-robin, because a flood fill over
 * a large cave is expensive and pressure is a slow phenomenon — nobody notices
 * that the steam room took four extra frames to decide to explode.
 *
 * The payoff is the acid-in-a-pressure-vessel and coolant-flash-boil
 * interactions: heat a sealed volume, its pressure climbs, and the weakest
 * bounding cell fails outward. Rooms blow their own walls out and that becomes
 * a route.
 */

import { profiler } from '../core/profiler';
import type { Rng } from '../core/rng';
import { Category, Flag, M, VOID } from './materials';
import { CHUNK_MASK, CHUNK_SHIFT, CellWorld } from './world';

/** Cap on flood-fill size. Beyond this the volume is treated as open air. */
const MAX_VOLUME = 3000;

/** Pressure contributed per °C above ambient, per gas cell. */
const THERMAL_PRESSURE = 0.9;

/**
 * Pressure at which a volume is *straining* — flagged PRESSURIZED, not yet
 * failing. Below it the flags come back off, because a strain indicator that
 * never clears is a lie, and docs/06 §4 is precisely about not lying with UI.
 */
const STRAIN = 40;

export interface RuptureEvent {
  x: number;
  y: number;
  pressure: number;
}

export class Pressure {
  private world: CellWorld;
  private rng: Rng;

  private queue = new Int32Array(MAX_VOLUME * 2);
  private visited = new Set<number>();
  private boundary: number[] = [];

  /** Round-robin cursor over active chunks. */
  private cursor = 0;

  /**
   * The one volume currently under strain, remembered by a cell inside it.
   *
   * Without this a sealed vessel can never fail. Motionless gas in a sealed
   * room touches nothing, so the chunk goes quiet after 8 ticks, drops out of
   * `activeChunks`, and the round-robin never looks at it again — it strains
   * forever. The evaluator has to keep watching the thing it just said was
   * about to explode. Alternating with the round-robin on tick parity means a
   * long-lived strain cannot starve every other volume in the world.
   */
  private watching = false;
  private watchX = 0;
  private watchY = 0;

  onRupture: ((e: RuptureEvent) => void) | null = null;

  /**
   * Work units spent this tick: one per flood-fill cell dequeued, one per
   * neighbour probed, one per strain flag examined, one per boundary cell.
   *
   * `MAX_VOLUME` and "one volume per tick" were always the quota; nothing
   * counted against them, so the budget fixture could only assert milliseconds.
   * The strain passes are counted too because they are two more full traversals
   * of the same volume and were the part that actually blew up — the flood fill
   * is bounded at 3000 cells, and `setStrain` walks all 3000 again.
   */
  workThisTick = 0;

  /**
   * Ceiling on `workThisTick`. Derived, not tuned: one volume per tick, at most
   * `MAX_VOLUME` cells, each dequeued once (1) with its four neighbours probed
   * (4), then re-walked once by the strain count (1) and once by `setStrain`
   * (1); `setStrain` also walks a boundary that a cubic volume bounds at two
   * cells per volume cell (2).
   */
  readonly maxWorkPerTick = MAX_VOLUME * 9;

  constructor(world: CellWorld, rng: Rng) {
    this.world = world;
    this.rng = rng;
  }

  step(): void {
    profiler.begin('pressure');
    this.workThisTick = 0;

    if (this.watching && (this.world.tick & 1) === 1) {
      // `peek` answers VOID for space that was never generated, and VOID is a
      // legal volume cell — so check the chunk is actually resident first, or a
      // watch that outlived a chunk eviction would flood-fill (and generate)
      // three thousand cells of empty world.
      const resident = this.world.peekChunkAt(this.watchX, this.watchY) !== null;
      const m = resident ? this.world.peek(this.watchX, this.watchY) : -1;
      if (resident && (m === VOID || M.category[m] === Category.GAS)) {
        this.evaluateVolume(this.watchX, this.watchY);
        profiler.end('pressure');
        return;
      }
      this.watching = false;
    }

    const active = this.world.activeChunks;
    if (active.length === 0) {
      profiler.end('pressure');
      return;
    }

    // One chunk per tick. A seed cell is picked from the chunk's gas content.
    this.cursor = (this.cursor + 1) % active.length;
    const c = active[this.cursor];
    if (c.nonEmpty === 0) {
      profiler.end('pressure');
      return;
    }

    const baseX = c.cx << CHUNK_SHIFT;
    const baseY = c.cy << CHUNK_SHIFT;

    // Find a gas cell to seed from. Sampling rather than scanning keeps this
    // O(1) — a volume worth exploding will be hit within a few ticks.
    let sx = -1;
    let sy = -1;
    for (let attempt = 0; attempt < 24; attempt++) {
      const lx = this.rng.u32() & CHUNK_MASK;
      const ly = this.rng.u32() & CHUNK_MASK;
      const m = c.mat[(ly << CHUNK_SHIFT) | lx];
      if (m !== VOID && M.category[m] === Category.GAS) {
        sx = baseX + lx;
        sy = baseY + ly;
        break;
      }
    }
    if (sx < 0) {
      profiler.end('pressure');
      return;
    }

    this.evaluateVolume(sx, sy);
    profiler.end('pressure');
  }

  /**
   * Flood the connected gas volume from a seed. Returns early (treating the
   * volume as vented) if it escapes the size cap — an open cave has no
   * pressure, and that early-out is what keeps this cheap.
   */
  private evaluateVolume(sx: number, sy: number): void {
    const w = this.world;
    this.visited.clear();
    this.boundary.length = 0;

    let head = 0;
    let tail = 0;
    this.queue[tail * 2] = sx;
    this.queue[tail * 2 + 1] = sy;
    tail++;
    this.visited.add(key(sx, sy));

    let totalPressure = 0;
    let cells = 0;

    while (head < tail) {
      const x = this.queue[head * 2];
      const y = this.queue[head * 2 + 1];
      head++;
      cells++;
      // One dequeue plus the four neighbour probes below.
      this.workThisTick += 5;

      const t = w.getTemp(x, y);
      const m = w.peek(x, y);
      totalPressure += Math.max(0, t - 20) * THERMAL_PRESSURE * (1 + M.reactivity[m]);

      for (let d = 0; d < 4; d++) {
        const nx = x + (d === 0 ? 1 : d === 1 ? -1 : 0);
        const ny = y + (d === 2 ? 1 : d === 3 ? -1 : 0);
        const k = key(nx, ny);
        if (this.visited.has(k)) continue;

        const nm = w.peek(nx, ny);
        const isGas = nm === VOID || M.category[nm] === Category.GAS;

        if (isGas) {
          if (tail >= MAX_VOLUME) {
            // Vented — too large to pressurise. Anything still flagged from a
            // previous, smaller evaluation is now wrong; drop it.
            this.watching = false;
            this.setStrain(false, head, -1, -1);
            return;
          }
          this.visited.add(k);
          this.queue[tail * 2] = nx;
          this.queue[tail * 2 + 1] = ny;
          tail++;
        } else {
          // Non-sealed solids leak; only SEALED material holds pressure.
          if ((M.tags[nm] & 0x04000000) === 0) {
            // A volume that found a leak is not under strain any more — most
            // often because the player just opened it. Say so.
            this.watching = false;
            this.setStrain(false, head, -1, -1);
            return;
          }
          this.visited.add(k);
          this.boundary.push(nx, ny);
        }
      }
    }

    if (cells === 0 || this.boundary.length === 0) return;

    const pressure = totalPressure / cells;

    // Find the weakest bounding cell first: it is both the cell that will fail
    // and the cell that has to *show* it is about to.
    let weakestX = -1;
    let weakestY = -1;
    let weakestThreshold = Infinity;
    for (let i = 0; i < this.boundary.length; i += 2) {
      const bx = this.boundary[i];
      const by = this.boundary[i + 1];
      const bm = w.peek(bx, by);
      if (M.indestructible[bm] === 1) continue;
      const thr = M.ruptureThreshold[bm];
      if (thr < weakestThreshold) {
        weakestThreshold = thr;
        weakestX = bx;
        weakestY = by;
      }
    }

    if (pressure < STRAIN) {
      this.watching = false;
      this.setStrain(false, tail, weakestX, weakestY);
      return;
    }
    this.watching = true;
    this.watchX = sx;
    this.watchY = sy;

    // Was this volume already showing strain when we arrived? Answer it BEFORE
    // flagging, because it is the whole telegraph: a volume gets at least one
    // full evaluation visibly PRESSURIZED before anything blows out. A wall
    // that fails on the same evaluation that first flagged it is the least
    // readable outcome in the game, and docs/06 §4 exists to prevent that.
    //
    // Asked of the volume rather than of the weakest wall cell on purpose:
    // which wall cell is "weakest" depends on the order the flood fill happened
    // to reach equally weak candidates, so gating on that one cell means a
    // vessel with a uniform wall is re-warned forever and never fails.
    let strained = 0;
    this.workThisTick += tail;
    for (let i = 0; i < tail; i++) {
      if ((w.getFlags(this.queue[i * 2], this.queue[i * 2 + 1]) & Flag.PRESSURIZED) !== 0) {
        strained++;
      }
    }
    const warned = strained * 2 >= cells;

    this.setStrain(true, tail, weakestX, weakestY);

    if (warned && weakestX >= 0 && pressure > weakestThreshold) {
      const bm = w.peek(weakestX, weakestY);
      const shattered = M.shatteredInto[bm];
      if (shattered >= 0) w.transmute(weakestX, weakestY, shattered);
      else w.set(weakestX, weakestY, VOID);
      // The blast widens the breach and throws the surrounding wall.
      w.damageCircle(weakestX, weakestY, 2 + pressure / 400, 4);
      if (this.onRupture) {
        this.onRupture({ x: weakestX, y: weakestY, pressure });
      }
    }
  }

  /**
   * Raise or lower PRESSURIZED across the volume and its weakest wall cell.
   *
   * The volume is read straight out of the BFS queue rather than by decoding
   * the `visited` keys. That is not only cheaper — decoding was where the bug
   * lived. `key()` shifts `x + 32768` left by 16, which sets the int32 sign bit
   * for every `x >= 0`, and the old decode used an arithmetic `>>`, recovering
   * `x - 65536`. Every flag landed 65536 cells away in ungenerated space, so
   * PRESSURIZED was never observably set on anything and a sealed steam box
   * blew its wall out with no warning frame at all.
   *
   * Flags are only written when they actually change: `addFlag`/`clearFlag`
   * touch the chunk, and touching 3000 cells every evaluation would keep a
   * quiet room permanently awake.
   *
   * Raising is restricted to gas and empty space — a strain glyph on a puddle
   * means nothing. Clearing is not: steam that condensed inside a pressurised
   * vessel carries the flag onto the water it becomes (`transmute` preserves
   * flags), and a stale strain marker on a cell nobody re-evaluates is exactly
   * the two-months-of-lying-UI failure docs/06 §4 is about.
   */
  private setStrain(on: boolean, count: number, wx: number, wy: number): void {
    const w = this.world;
    // `boundary` is flat (x, y) pairs, so its iteration count is half its length.
    this.workThisTick += count + (this.boundary.length >> 1);
    for (let i = 0; i < count; i++) {
      this.mark(on, this.queue[i * 2], this.queue[i * 2 + 1]);
    }
    for (let i = 0; i < this.boundary.length; i += 2) {
      // Boundary cells only ever get the flag via `wx`/`wy` below, but they
      // must still be cleared: the wall that was straining is the wall the
      // player is watching.
      if (!on) this.mark(false, this.boundary[i], this.boundary[i + 1]);
    }
    if (wx >= 0) this.mark(on, wx, wy);
  }

  private mark(on: boolean, x: number, y: number): void {
    const w = this.world;
    const has = (w.getFlags(x, y) & Flag.PRESSURIZED) !== 0;
    if (on) {
      if (has) return;
      const m = w.peek(x, y);
      if (m !== VOID && M.category[m] !== Category.GAS) return;
      w.addFlag(x, y, Flag.PRESSURIZED);
    } else if (has) {
      w.clearFlag(x, y, Flag.PRESSURIZED);
    }
  }
}

/**
 * Cell key for the visited set. Only membership matters here, so all this has
 * to be is injective over ±32768 — but it is written unsigned-safe so a future
 * reader who wants to decode it gets the right answer (`>>> 16`, not `>> 16`).
 */
function key(x: number, y: number): number {
  return (((x + 32768) << 16) | (y + 32768)) >>> 0;
}
